import { Button } from '@sunwebgroup/uds-ui/button'
import { TextField } from '@sunwebgroup/uds-ui/fields/text-field'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import * as React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useIntl } from '@/intl'
import { CheckoutSection } from '@/scopes/checkout/components/CheckoutSection'
import { useSendCouponCodeEvent } from '@/scopes/checkout/domain/analytics/useSendCouponCode'
import { useCouponCode } from '@/scopes/checkout/domain/hooks'
import styles from './CouponCodeForm.module.css'
import { useCouponStore } from './store'

interface FormValues {
  couponCode: string
}

export function CouponCodeForm() {
  const intl = useIntl()
  const { setCouponCode } = useCouponCode()

  const [isChecking, setIsChecking] = React.useState(false)

  const onSubmit = (result: FormValues) => {
    setIsChecking(true)
    setCouponCode(result.couponCode)
  }

  const couponInfo = useCouponStore(state => state.couponInfo)

  React.useEffect(() => {
    if (couponInfo?.success) {
      setIsChecking(false)
    }
    else if (couponInfo?.success === false) {
      setIsChecking(false)
    }
  }, [couponInfo])

  const { control, handleSubmit } = useForm<FormValues>()

  // Determine status without nested ternary
  let status: 'error' | 'success' | undefined
  useSendCouponCodeEvent((couponInfo && couponInfo.success) ? couponInfo.value : undefined)
  if (couponInfo != null && !couponInfo.success) {
    status = 'error'
  }
  else if (couponInfo != null && couponInfo.success) {
    status = 'success'
  }
  else {
    status = undefined
  }

  return (
    <CheckoutSection
      title={intl.formatMessage({ id: 'checkout_steps_coupon_code_title' })}
      description={intl.formatMessage({ id: 'checkout_steps_coupon_code_description' })}
      glyph="TicketWithPercentage"
    >
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.textFieldWrapper}>
          <Controller
            name="couponCode"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                status={status}
                disabled={isChecking}
                feedbackText={
                  couponInfo != null && !couponInfo.success
                    ? intl.formatMessage({ id: 'checkout_steps_coupon_code_error' })
                    : undefined
                }
                ariaLabel={intl.formatMessage({ id: 'checkout_steps_coupon_code_aria_label' })}
              />
            )}
          />
          {couponInfo != null && couponInfo.success && (
            <Typography variant="body-s-regular-loose" className={styles.feedbackText}>
              {intl.formatMessage({ id: 'checkout_steps_coupon_code_success' })}
            </Typography>
          )}
        </div>
        <Button className={styles.submitButton} variant="secondary" type="submit" disabled={isChecking}>
          {intl.formatMessage({ id: 'checkout_steps_coupon_code_submit' })}
        </Button>
      </form>
    </CheckoutSection>
  )
}
